import { Check, Copy, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { api, type CollectionActivation, type Conference, type SubmissionPhaseDraft } from "../../api.js";
import { SetupTemplateChooser } from "../setup/SetupTemplateChooser.js";
import { SubmissionWorkspacePreview } from "../../workspaces/SubmissionWorkspacePreview.js";
import { StageHeader } from "./StageScaffold.js";
import { SubmissionInspection } from "../submission/SubmissionInspection.js";
import { apiErrorMessage } from "../../../api/client.js";
import { Button } from "../../../design/components/Button.js";
import { Alert, Skeleton } from "../../../design/components/Feedback.js";
import { ResponsiveSheet } from "../../../design/components/ResponsiveSheet.js";

export function CollectStage({ conference }: { conference: Conference }) {
  const [phase, setPhase] = useState<SubmissionPhaseDraft | null | undefined>(undefined);
  const [setupOpen, setSetupOpen] = useState(false);
  const [activation, setActivation] = useState<CollectionActivation | null | undefined>(undefined);
  const [activating, setActivating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setPhase(undefined);
    setSetupOpen(false);
    setActivation(undefined);
    setCopied(false);
    setError("");
    api.submissionPhase(conference.id, conference.organization_id)
      .then(async (saved) => {
        if (!active) return;
        setPhase(saved);
        setSetupOpen(saved === null);
        if (saved) {
          try {
            const status = await api.collectionStatus(conference.id, conference.organization_id);
            if (active) setActivation(status);
          } catch (reason) {
            if (active) {
              setActivation(null);
              setError(apiErrorMessage(reason, "The submission opening status could not be loaded."));
            }
          }
        } else {
          setActivation(null);
        }
      })
      .catch((reason) => {
        if (!active) return;
        setError(apiErrorMessage(reason, "The collection workflow could not be loaded."));
        setPhase(null);
      });
    return () => { active = false; };
  }, [conference.id, conference.organization_id]);

  const completeSetup = (saved: SubmissionPhaseDraft) => {
    setPhase(saved);
    setActivation({
      ready: false,
      entry_path: `/c/${conference.organization_id}/${conference.public_slug}/submit`,
      phase_status: saved.status,
      workflow_status: "DRAFT",
    });
    setSetupOpen(false);
  };

  const activate = async () => {
    setActivating(true);
    setError("");
    try {
      const result = await api.activateCollection(conference.id, conference.organization_id);
      if (!result.ready) {
        throw new Error("Submissions were published but the author page did not open. Please try again.");
      }
      setActivation(result);
      setPhase((current) => current ? { ...current, status: "PUBLISHED" } : current);
    } catch (reason) {
      setError(apiErrorMessage(reason, "Submissions could not be opened. Your workflow is still saved."));
    } finally {
      setActivating(false);
    }
  };

  const copyEntryLink = async () => {
    if (!activation?.entry_path) return;
    try {
      await navigator.clipboard.writeText(new URL(activation.entry_path, window.location.origin).toString());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setError("The author link could not be copied. Open the page and copy its address instead.");
    }
  };

  return <div className="conference-stage">
    <StageHeader title="Collect" description="Choose the submission journey, set its dates, and manage the saved phase draft." />
    {error ? <Alert tone="danger">{error}</Alert> : null}
    {phase === undefined ? <div className="conference-stage-loading" aria-label="Loading collection workflow"><Skeleton /><Skeleton /></div> : phase === null ? <section className="collect-setup-gate"><span>Step 1</span><h2>Choose how submissions will move through the conference</h2><p>Select a guided workflow, confirm its dates, and Conferis will prepare the first author phase automatically.</p><Button onClick={() => setSetupOpen(true)}>Choose workflow</Button></section> : <><SubmissionWorkspacePreview conference={conference} initialPhase={phase} />
      <section className={["collection-launch", activation?.ready ? "is-ready" : ""].filter(Boolean).join(" ")}>
        <div className="collection-launch__copy">
          <span>{activation?.ready ? "Submissions open" : "Final step"}</span>
          <h2>{activation?.ready ? "Author submission page is open" : "Open submissions to authors"}</h2>
          <p>{activation?.ready ? "Share this secure conference link with authors. New drafts will appear in this workspace." : "Conferis will publish the saved form, phases, and workflow together, then open one conference-specific author page."}</p>
        </div>
        {activation?.ready ? <div className="collection-launch__actions">
          <a className="ui-button ui-button--primary" href={activation.entry_path} target="_blank" rel="noreferrer"><ExternalLink size={16} aria-hidden="true" /><span className="ui-button__content">Open author page</span></a>
          <Button variant="secondary" onClick={copyEntryLink}>{copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}{copied ? "Copied" : "Copy author link"}</Button>
        </div> : <Button onClick={activate} busy={activating} disabled={activation === undefined}>Open submissions</Button>}
      </section>
      <SubmissionInspection conference={conference} /></>}
    <ResponsiveSheet open={setupOpen} title="Set up collection workflow" onClose={() => setSetupOpen(false)} layout="modal">
      <SetupTemplateChooser conference={conference} embedded onCompleted={completeSetup} />
    </ResponsiveSheet>
  </div>;
}
